import {BookingPracticeAppointmentReasonFieldsFragment} from "@page-containers/booking/graphql/__generated__/FetchAppointmentReasons.type";
import {useNextAvailable, WithNextSlot} from "@components/Booking/useNextAvailable";
import {useRouter} from "next/router";
import {buildBookingLink, parseBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {useLocationsQuery} from "@components/Booking/LocationSelection/__generated__/bookable-location.type";
import {PracticeId} from "../../../../constants/practiceIds";
import React, {useCallback} from "react";
import RegionTreeSelector from "@components/Region/RegionTreeSelector";
import {Row} from "@components/Booking/LocationSelection/LocationRow";
import HeadingWithReason from "@components/Booking/LocationSelection/HeadingWithReason";
import HeadingWithoutReason from "@components/Booking/LocationSelection/HeadingWithoutReason";
import {useFetchRegionsQuery} from "@components/Booking/LocationSelection/__generated__/fetch-region-info.type";
import {TreeItem} from "@components/components/Tree/ComplexTreeWrapper";
import {Region} from "@components/Booking/LocationSelection/types";
import {
  useWithDistanceFromUser,
  WithDistance,
} from "@components/Booking/LocationSelection/useWithDistanceFromUser";
import RowLoading from "@components/Booking/LocationSelection/LocationRow/RowLoading";

type Props = {
  practiceAppointmentReason?: BookingPracticeAppointmentReasonFieldsFragment;
  specialtyId: string;
};

const withAvailabilityFirst = (a: WithNextSlot<unknown>, b: WithNextSlot<unknown>) => {
  if (!a.nextSlot) {
    return 1;
  }
  if (!b.nextSlot) {
    return -1;
  }
  return a.nextSlot.time - b.nextSlot.time;
};

const withClosestFirst = (a: WithDistance<unknown>, b: WithDistance<unknown>) => {
  if (!a.distance) {
    return 1;
  }
  if (!b.distance) {
    return -1;
  }
  return a.distance - b.distance;
};

const LocationSelectionPage = ({practiceAppointmentReason, specialtyId}: Props) => {
  const router = useRouter();
  const parsedQuery = parseBookingQuery(router.query);
  const shouldLoadRegions = parsedQuery.practiceId === PracticeId.CARBON;
  const {data: locations, loading: locationsLoading} = useLocationsQuery({
    variables: {
      practiceId: parsedQuery.practiceId,
      region: shouldLoadRegions ? parsedQuery.region : null,
    },
  });
  const {data: regionData} = useFetchRegionsQuery({
    variables: {
      practiceId: parsedQuery.practiceId,
    },
    skip: !shouldLoadRegions,
  });
  const regions = regionData?.practice?.regions?.nodes;

  const onRegionChange = useCallback(
    async (slug: string) => {
      await router.replace(buildBookingLink({...parsedQuery, region: slug}));
    },
    [parsedQuery, router],
  );

  const {data: locationsWithSlots, isLoading: slotsLoading} = useNextAvailable({
    specialtyId,
    locations: locations?.practice?.locations?.nodes,
    reasonId: practiceAppointmentReason?.appointmentReason?.id,
  });
  const {data: locationAddressesWithDistance} = useWithDistanceFromUser(locationsWithSlots ?? []);
  // .slice() shallow copy prevents crashes when using the browser back & forward buttons.
  // Replace this with toSorted() when our TS versions support it.
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/toSorted
  const sorted = locationAddressesWithDistance
    ?.slice()
    ?.sort(withClosestFirst)
    ?.sort(withAvailabilityFirst);

  const apptReason = practiceAppointmentReason?.appointmentReason?.name;

  const getHref = useCallback(
    (item: TreeItem<string>) => {
      if (item.children?.length) {
        return;
      }
      return buildBookingLink({...parsedQuery, region: `${item.id}`});
    },
    [parsedQuery],
  );

  const usefulRegions = regions?.filter(isUsefulRegion);

  return (
    <>
      {apptReason ? <HeadingWithReason apptReason={apptReason} /> : <HeadingWithoutReason />}
      {shouldLoadRegions && usefulRegions && usefulRegions.length && (
        // Hacky negative margin here b/c the region tree selector mixes form with function,
        // and this is a quick way to make the UX look good w/o much effort.
        // It also looks like we _may_ want to purge regions from this screen altogether,
        // so I don't want to invest much into it.
        <div className="-mx-3">
          <RegionTreeSelector
            onSelect={onRegionChange}
            regions={usefulRegions}
            getHref={getHref}
            selectedRegionSlug={parsedQuery.region ?? usefulRegions[0].slug}
          />
        </div>
      )}
      {locationsLoading ? (
        <ul>
          {Array.from({length: 5}).map((_, i) => (
            <RowLoading key={i} />
          ))}
        </ul>
      ) : (
        <ul>
          {sorted?.map(location => {
            const href = location?.nextSlot
              ? buildBookingLink({...parsedQuery, locationSlug: location.slug})
              : undefined;

            return (
              <Row
                key={location.name}
                href={href}
                location={location}
                slotsLoading={slotsLoading}
              />
            );
          })}
        </ul>
      )}
    </>
  );
};

const isUsefulRegion = (region: Region): region is {id: string; name: string; slug: string} =>
  !!region.name && !!region.slug;

export default React.memo(LocationSelectionPage);
